import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  effect,
  inject,
  untracked,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Media } from '@core/media';
import { NavigationService } from '@layout/data/navigation';
import { Notifications } from '@layout/ui/notifications';
import { AppSecondarySidebar } from '@layout/ui/secondary-sidebar';
import { AppSidebar } from '@layout/ui/sidebar';
import { User } from '@layout/ui/user';

@Component({
  selector: 'admin-layout',
  imports: [
    NgTemplateOutlet,
    RouterOutlet,
    MatSidenavModule,
    AppSidebar,
    AppSecondarySidebar,
    MatIconButton,
    Notifications,
    User,
    MatIcon,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let navItem = nav.selectedNavItem();
    <mat-sidenav-container
      class="print:h-auto print:overflow-visible"
      (backdropClick)="nav.closePanel()"
    >
      <!-- Primary sidenav: icon rail + secondary panel on mobile -->
      <mat-sidenav
        class="print:hidden"
        [style.--sidenav-width]="isMobile() && navItem ? '20rem' : '3rem'"
        [mode]="isMobile() ? 'over' : 'side'"
        [opened]="!isMobile()"
        [disableClose]="!isMobile()"
        fixedInViewport
        #sidenav
      >
        <app-sidebar />
        <!-- Mobile: secondary panel inside the same sidenav -->
        @if (isMobile() && navItem) {
          <app-secondary-sidebar
            [title]="navItem.tooltip"
            [items]="nav.selectedItems()!"
            (itemClick)="onMobileItemClick()"
          />
        }
      </mat-sidenav>
      <mat-sidenav-content
        class="flex flex-col overflow-hidden bg-transparent print:ml-0 print:h-auto print:overflow-visible"
      >
        <!-- Desktop: secondary panel as nested sidenav overlay -->
        @if (!isMobile()) {
          <mat-sidenav-container
            class="print:h-auto print:overflow-visible"
            (backdropClick)="nav.closePanel()"
          >
            <mat-sidenav
              class="w-75"
              mode="over"
              [opened]="!!navItem"
            >
              @if (navItem) {
                <app-secondary-sidebar
                  [title]="navItem.tooltip"
                  [items]="nav.selectedItems()!"
                  (itemClick)="nav.closePanel()"
                />
              }
            </mat-sidenav>
            <mat-sidenav-content
              class="print:ml-0 print:h-auto print:overflow-visible"
            >
              <ng-container *ngTemplateOutlet="pageContent" />
            </mat-sidenav-content>
          </mat-sidenav-container>
        }

        <!-- Mobile: no nested sidenav, just the content -->
        @if (isMobile()) {
          <ng-container *ngTemplateOutlet="pageContent" />
        }
      </mat-sidenav-content>
    </mat-sidenav-container>

    <!-- Shared page content template -->
    <ng-template #pageContent>
      <div
        class="flex flex-auto flex-col overflow-hidden bg-white dark:bg-neutral-2 print:overflow-visible"
      >
        <!-- Header -->
        <div class="flex items-center py-3 pr-5 pl-4 lg:hidden print:hidden">
          <!-- Sidebar toggle button -->
          <button
            matIconButton
            (click)="sidenav.toggle()"
          >
            <mat-icon svgIcon="panel-left" />
          </button>

          <!-- Spacer -->
          <div class="flex-auto"></div>

          <!-- Notifications -->
          <notifications position="bottom-end" />

          <!-- User -->
          <user compact />
        </div>

        <!-- Content -->
        <router-outlet />
      </div>
    </ng-template>
  `,
})
export default class AdminLayout {
  // Dependencies
  private media = inject(Media);
  private router = inject(Router);
  protected nav = inject(NavigationService);

  // Queries
  readonly sidenav = viewChild.required<MatSidenav>('sidenav');

  // State
  private routerEvent = toSignal(this.router.events);
  protected isMobile = this.media.match(`(width < 64rem)`);

  constructor() {
    // Close sidenav on navigation (mobile only)
    effect(() => {
      const event = this.routerEvent();
      const isMobile = untracked(() => this.isMobile());

      if (!(event instanceof NavigationEnd) || !isMobile) {
        return;
      }

      untracked(() => this.sidenav().toggle(false));
    });
  }

  protected onMobileItemClick() {
    this.nav.closePanel();
    this.sidenav().toggle(false);
  }
}
